import { useController, type Control, type FieldValues, type Path } from "react-hook-form";

import { RangePicker, type DateRange, type RangePickerMessages, type RangePickerSize } from "ui/range-picker";

import { toDayjs, toFormDate } from "utilities/date";

type FormBuilderDateRangeFieldProps<T extends FieldValues> = {
  control: Control<T>;
  startName: Path<T>;
  endName: Path<T>;
  groupLabel?: string;
  startLabel: string;
  endLabel: string;
  errMessage: RangePickerMessages;
  required: boolean;
  disabled: boolean;
  size: RangePickerSize;
  infoTooltip?: string;
};

export const FormBuilderDateRangeField = <T extends FieldValues>({
  control,
  startName,
  endName,
  groupLabel,
  startLabel,
  endLabel,
  errMessage,
  required,
  disabled,
  size,
  infoTooltip,
}: FormBuilderDateRangeFieldProps<T>) => {
  const { field: startField } = useController({ control, name: startName });
  const { field: endField } = useController({ control, name: endName });

  const handleChange = ([startDate, endDate]: DateRange) => {
    startField.onChange(toFormDate(startDate));
    endField.onChange(toFormDate(endDate));
  };

  // RangePicker has already emptied the field through `onChange`; clearing still counts as a touch.
  const handleClear = () => {
    startField.onBlur();
    endField.onBlur();
  };

  return (
    <RangePicker
      ref={startField.ref}
      name={startName}
      label={groupLabel}
      value={[toDayjs(startField.value), toDayjs(endField.value)]}
      localeText={{ start: startLabel, end: endLabel }}
      errMessage={errMessage}
      required={required}
      disabled={disabled}
      size={size}
      infoTooltip={infoTooltip}
      onChange={handleChange}
      onClear={handleClear}
      onBlur={startField.onBlur}
    />
  );
};
